import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Smartphone, RefreshCw, CheckCircle2, AlertTriangle, MinusCircle,
  Send, Play, X, FlaskConical, Globe2,
} from "lucide-react";
import { API } from "../../config";

/**
 * PassRefreshHistoryWidget
 *
 * Surfaces the audit log of Apple Wallet APN refresh broadcasts and
 * lets Super Admins trigger a new broadcast (smoke-test first, real
 * broadcast after visual validation on iPhone).
 *
 * Backend:
 *   GET  /api/admin/apple-passkit/refresh-history?limit=5
 *   POST /api/admin/apple-passkit/refresh-broadcast
 */

const STATUS_THEME = {
  ok:      { text: "text-green-300", bg: "bg-green-500/5",  border: "border-green-500/30",  Icon: CheckCircle2,   label: "OK" },
  partial: { text: "text-amber-300", bg: "bg-amber-500/5",  border: "border-amber-500/30",  Icon: AlertTriangle,  label: "Parziale" },
  failed:  { text: "text-red-300",   bg: "bg-red-500/5",    border: "border-red-500/30",    Icon: AlertTriangle,  label: "Fallito" },
  empty:   { text: "text-gray-400",  bg: "bg-black/30",     border: "border-gray-700",     Icon: MinusCircle,    label: "Nessun device" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const PassRefreshHistoryWidget = ({ isSuperAdmin = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchHistory = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem("lojack_token") || "";
      const r = await axios.get(`${API}/admin/apple-passkit/refresh-history?limit=5`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento storico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid="pass-refresh-loading">
        <p className="text-gray-500 text-sm flex items-center gap-2">
          <RefreshCw size={14} className="animate-spin" /> Carico storico refresh Apple Wallet…
        </p>
      </div>
    );
  }

  return (
    <div
      className="bg-[#111] border border-gray-800 rounded-2xl p-5 space-y-4"
      data-testid="pass-refresh-history-widget"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Smartphone size={16} className="text-lojack-red" />
            Apple Wallet — Pass Refresh History
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Ultimi 5 broadcast APN ai pass installati · Smoke-test esclusi dalla vista default
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchHistory}
            className="text-gray-500 hover:text-gray-300 p-1"
            aria-label="Aggiorna"
            data-testid="pass-refresh-history-reload"
          >
            <RefreshCw size={14} />
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setModalOpen(true)}
              className="bg-lojack-red hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
              data-testid="pass-refresh-broadcast-open"
            >
              <Send size={12} /> Refresh ora
            </button>
          )}
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm" data-testid="pass-refresh-history-error">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!error && rows.length === 0 && (
        <div className="bg-black/30 border border-gray-800 rounded-lg p-4 text-gray-500 text-sm text-center" data-testid="pass-refresh-history-empty">
          Nessun broadcast registrato. Lancia il primo refresh da &quot;Refresh ora&quot; dopo aver fatto smoke-test sul tuo iPhone.
        </div>
      )}

      {/* Rows */}
      {rows.length > 0 && (
        <div className="space-y-2" data-testid="pass-refresh-history-rows">
          {rows.map((row) => {
            const theme = STATUS_THEME[row.status] || STATUS_THEME.empty;
            const StatusIcon = theme.Icon;
            return (
              <div
                key={row.id}
                className={`${theme.bg} border ${theme.border} rounded-lg p-3 flex items-start gap-3`}
                data-testid={`pass-refresh-row-${row.id}`}
              >
                <div className={`w-9 h-9 rounded-full ${theme.bg} flex-shrink-0 flex items-center justify-center border ${theme.border}`}>
                  <StatusIcon size={16} className={theme.text} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium truncate" title={row.reason}>
                      {row.reason}
                    </p>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${theme.text} ${theme.bg} border ${theme.border}`}>
                      {theme.label}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {fmtDate(row.started_at)} · {row.triggered_by}
                  </p>
                  <div className="flex items-center gap-3 text-xs mt-1.5 flex-wrap">
                    <span className="text-gray-300">
                      <b className="text-white">{row.devices_total}</b> device
                    </span>
                    <span className="text-green-300">
                      ✓ <b>{row.devices_pushed_ok}</b>
                    </span>
                    {row.devices_pushed_failed > 0 && (
                      <span className="text-red-300">
                        ✗ <b>{row.devices_pushed_failed}</b>
                      </span>
                    )}
                    {row.devices_skipped_inactive > 0 && (
                      <span className="text-gray-500">
                        – <b>{row.devices_skipped_inactive}</b> inattivi
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <RefreshBroadcastModal
          onClose={() => setModalOpen(false)}
          onDone={() => { setModalOpen(false); fetchHistory(); }}
        />
      )}
    </div>
  );
};

// ─── Modal: trigger a broadcast ───────────────────────────────────────

const RefreshBroadcastModal = ({ onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [smokeTest, setSmokeTest] = useState(true); // safer default
  const [smokeTestSerials, setSmokeTestSerials] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!reason || reason.trim().length < 3) {
      toast.error("Inserisci una motivazione (min. 3 caratteri)");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("lojack_token") || "";
      const body = { reason: reason.trim() };
      if (smokeTest) body.smoke_test = true;
      if (smokeTestSerials.trim()) body.smoke_test_serials = smokeTestSerials.trim();
      if (!smokeTest && countryFilter.trim()) body.country_filter = countryFilter.trim().toUpperCase();

      const r = await axios.post(`${API}/admin/apple-passkit/refresh-broadcast`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(r.data);
      const tag = r.data.is_smoke_test ? "Smoke-test" : "Broadcast";
      toast.success(`${tag} completato: ${r.data.devices_pushed_ok}/${r.data.devices_total} device aggiornati`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore broadcast");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="pass-refresh-broadcast-modal"
    >
      <div
        className="bg-[#111] border border-gray-800 rounded-2xl p-6 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Send size={16} className="text-lojack-red" /> Trigger Apple Wallet Refresh
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Invia push silenzioso APN ai pass installati. Smoke-test caldamente raccomandato prima del broadcast completo.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white p-1"
            data-testid="pass-refresh-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {result ? (
          <ResultPanel result={result} onDone={onDone} />
        ) : (
          <>
            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider">Motivazione</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="es. iOS field display hotfix"
                className="mt-1 w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-lojack-red"
                data-testid="pass-refresh-modal-reason"
              />
            </div>

            <div className="space-y-2">
              <label
                className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors ${smokeTest ? "border-amber-500/30 bg-amber-500/5" : "border-gray-700 bg-black/30"}`}
                data-testid="pass-refresh-modal-smoke-toggle"
              >
                <input
                  type="checkbox"
                  checked={smokeTest}
                  onChange={(e) => setSmokeTest(e.target.checked)}
                  className="accent-amber-500"
                />
                <FlaskConical size={14} className="text-amber-300" />
                <span className="text-sm text-white">Smoke-test (raccomandato)</span>
              </label>
              <p className="text-gray-500 text-xs pl-7">
                Push solo ai serial in <code className="text-amber-300">APN_SMOKE_TEST_SERIALS</code> (env). Override sotto.
              </p>
            </div>

            {smokeTest && (
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wider">Smoke-test serials (override, opzionale)</label>
                <input
                  type="text"
                  value={smokeTestSerials}
                  onChange={(e) => setSmokeTestSerials(e.target.value)}
                  placeholder="customer-uuid-fab,customer-uuid-mara"
                  className="mt-1 w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                  data-testid="pass-refresh-modal-serials"
                />
              </div>
            )}

            {!smokeTest && (
              <div>
                <label className="text-gray-400 text-xs uppercase tracking-wider flex items-center gap-1">
                  <Globe2 size={11} /> Country filter (opzionale)
                </label>
                <input
                  type="text"
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  placeholder="IT"
                  maxLength={2}
                  className="mt-1 w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm uppercase focus:outline-none focus:border-lojack-red"
                  data-testid="pass-refresh-modal-country"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Lascia vuoto per broadcast globale a tutti i paesi.
                </p>
              </div>
            )}

            {!smokeTest && (
              <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-2.5 flex gap-2 text-xs text-red-300">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>
                  <b>Broadcast reale</b>: invierà push a tutti i pass installati. Verifica prima il rendering con smoke-test.
                </span>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-sm px-4 py-2 rounded-lg"
                data-testid="pass-refresh-modal-cancel"
              >
                Annulla
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className={`text-white text-sm px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${smokeTest ? "bg-amber-600 hover:bg-amber-700" : "bg-lojack-red hover:bg-red-700"}`}
                data-testid="pass-refresh-modal-submit"
              >
                {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
                {smokeTest ? "Lancia smoke-test" : "Lancia broadcast"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ResultPanel = ({ result, onDone }) => {
  const theme = STATUS_THEME[result.status] || STATUS_THEME.empty;
  const Icon = theme.Icon;
  return (
    <div className="space-y-3" data-testid="pass-refresh-modal-result">
      <div className={`${theme.bg} border ${theme.border} rounded-lg p-4 flex items-center gap-3`}>
        <Icon size={28} className={theme.text} />
        <div>
          <p className={`${theme.text} font-semibold text-sm`}>
            {result.is_smoke_test ? "Smoke-test completato" : "Broadcast completato"}
          </p>
          <p className="text-gray-400 text-xs">{theme.label}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-black/40 border border-gray-800 rounded-lg p-2">
          <p className="text-gray-500 text-[10px] uppercase">Totale</p>
          <p className="text-white text-lg font-bold">{result.devices_total}</p>
        </div>
        <div className="bg-black/40 border border-gray-800 rounded-lg p-2">
          <p className="text-gray-500 text-[10px] uppercase">Push OK</p>
          <p className="text-green-300 text-lg font-bold">{result.devices_pushed_ok}</p>
        </div>
        <div className="bg-black/40 border border-gray-800 rounded-lg p-2">
          <p className="text-gray-500 text-[10px] uppercase">Falliti</p>
          <p className={`text-lg font-bold ${result.devices_pushed_failed > 0 ? "text-red-300" : "text-gray-500"}`}>
            {result.devices_pushed_failed}
          </p>
        </div>
      </div>

      {result.is_smoke_test && result.status === "ok" && (
        <div className="bg-green-500/5 border border-green-500/30 rounded-lg p-2.5 text-xs text-green-300">
          ✅ Smoke-test riuscito. Verifica il rendering sul tuo iPhone (Wallet → tira giù la card), poi torna qui e disabilita &quot;Smoke-test&quot; per il broadcast completo.
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onDone}
          className="bg-gray-700 hover:bg-gray-600 text-white text-sm px-4 py-2 rounded-lg"
          data-testid="pass-refresh-modal-result-close"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
};

export default PassRefreshHistoryWidget;
